import { useState, useEffect } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import MarketingLayout from './MarketingLayout'
import Seo from './Seo'
import StoreBadges from './StoreBadges'
import ChevronDown from '../../Assets/landing/Chevron_Down.png'
import { getFeatureBySlug } from './featuresData'
import '../AppLandingPage.css'
import './Marketing.css'

/** Template for /features/:slug — renders one feature from featuresData. */
export default function FeaturePage() {
    const { slug } = useParams()
    const feature = getFeatureBySlug(slug)
    const [openFaqIndex, setOpenFaqIndex] = useState(null)

    useEffect(() => {
        window.scrollTo(0, 0)
        setOpenFaqIndex(null)
    }, [slug])

    if (!feature) {
        return <Navigate to="/features" replace />
    }

    const toggleFaq = (index) => {
        setOpenFaqIndex((prev) => (prev === index ? null : index))
    }

    const relatedFeatures = (feature.related || [])
        .map(getFeatureBySlug)
        .filter(Boolean)

    // FAQPage structured data for rich results
    const faqJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: feature.faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
    }

    return (
        <MarketingLayout>
            <Seo
                title={feature.seoTitle}
                description={feature.seoDescription}
                path={`/features/${feature.slug}`}
            />
            <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

            <div className="marketing-page">
                <div className="marketing-hero">
                    <p className="marketing-breadcrumb">
                        <Link to="/">Home</Link> &rsaquo; <Link to="/features">Features</Link> &rsaquo;{' '}
                        {feature.name}
                    </p>
                    <span
                        className={`marketing-plan-chip ${feature.plan === 'Free' ? 'free' : 'premium'}`}
                    >
                        {feature.plan === 'Free' ? 'Included free' : 'Premium feature'}
                    </span>
                    <h1>{feature.name}</h1>
                    <p className="marketing-hero-motto">{feature.tagline}</p>
                </div>
                <div className="hero-divider" />

                <div className="marketing-container">
                    <div className="feature-detail-hero">
                        <div className="feature-detail-hero-copy">
                            <h2>The fun way to plan your wedding day</h2>
                            <p>{feature.heroIntro}</p>
                            <StoreBadges style={{ marginTop: 20 }} />
                        </div>
                        <div
                            className={
                                feature.imageStyle === 'icon'
                                    ? 'feature-detail-hero-image icon-style'
                                    : 'feature-detail-hero-image'
                            }
                        >
                            <img src={feature.image} alt={feature.imageAlt} />
                        </div>
                    </div>
                </div>

                <section className="marketing-section marketing-section-alt">
                    <div className="marketing-container">
                        <h2>Why couples love it</h2>
                        <div className="benefit-grid">
                            {feature.benefits.map((benefit) => (
                                <div className="benefit-card" key={benefit.title}>
                                    <h3>{benefit.title}</h3>
                                    <p>{benefit.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="marketing-section">
                    <div className="marketing-container">
                        <h2>How it works</h2>
                        <ol className="steps-list">
                            {feature.howItWorks.map((step) => (
                                <li key={step}>{step}</li>
                            ))}
                        </ol>
                    </div>
                </section>

                <section className="marketing-section marketing-section-alt">
                    <h2>Frequently asked questions</h2>
                    <div className="faq-section">
                        {feature.faqs.map((faq, index) => (
                            <div
                                key={faq.q}
                                className={openFaqIndex === index ? 'faq-item faq-item-open' : 'faq-item'}
                                onClick={() => toggleFaq(index)}
                            >
                                <div className="faq-item-header">
                                    <h3>{faq.q}</h3>
                                    <img
                                        src={ChevronDown}
                                        alt=""
                                        className={`faq-chevron ${openFaqIndex === index ? 'open' : ''}`}
                                    />
                                </div>
                                <p className={openFaqIndex === index ? 'faq-answer-open' : 'faq-answer-closed'}>
                                    {faq.a}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>

                {relatedFeatures.length > 0 && (
                    <section className="marketing-section">
                        <div className="marketing-container">
                            <h2>Explore more features</h2>
                            <div className="related-features">
                                {relatedFeatures.map((related) => (
                                    <Link
                                        key={related.slug}
                                        className="related-feature-link"
                                        to={`/features/${related.slug}`}
                                    >
                                        {related.name}
                                    </Link>
                                ))}
                                <Link className="related-feature-link" to="/pricing">
                                    See pricing
                                </Link>
                            </div>
                        </div>
                    </section>
                )}

                <div className="marketing-cta-band">
                    <h2>Your dream wedding starts here</h2>
                    <p>The fun way to plan your wedding day</p>
                    <StoreBadges />
                </div>
            </div>
        </MarketingLayout>
    )
}
